import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { axios } from '../../libs/axios';
import { useTranslation } from '../../i18n/I18nContext';
import { fmtNum } from './charts';
import { ParamTooltip } from './ParamTooltip';
import { DisplayParamsSection } from './DisplayParamsSection';
import {
  normalizeWeights,
  normalizeMaturityWeights,
  expertiseQDelta,
  speedTable,
  ratingForSingle,
  dAtBreachRates,
  softmaxRatio,
  weightWarnings,
  fmtWeightBar,
  fmtMaturityWeightBar,
} from './paramsPure';
import { buildClassifyKey } from './classifyKey';
import {
  getClassifierModelCapabilities,
  defaultThinkingEffortForModel,
} from './classifierModelCapabilities';
import {
  estimateClassifierCost,
  formatCostUsd,
  formatContextWindow,
  groupCatalogueByProvider,
  resolvePromptForEstimate,
  estimateTokens,
  CLASSIFIER_FIELD_DETAILS,
  CLASSIFIER_FIELD_FORMATS,
  buildFieldListForPrompt,
  serializeFieldList,
} from './classifierCostPure';

function generateAutoName(baseName) {
  const base = (baseName || 'profile').replace(/\s+/g, '-');
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const stamp = [
    now.getFullYear(),
    pad(now.getMonth() + 1),
    pad(now.getDate()),
    '-',
    pad(now.getHours()),
    pad(now.getMinutes()),
    pad(now.getSeconds()),
  ].join('');
  return `${base}-${stamp}`;
}

function isApiError(result) {
  return result?.status >= 400 || (result?.message && !result?.profile && !result?.shifts);
}

function notifyMatchError(message, fallback) {
  const text = Array.isArray(message)
    ? message.join('; ')
    : (message || fallback || '');
  if (text) {
    window.nError?.({ text });
  }
}

function applyProfileToState(profile, setDraft, setTypesText) {
  if (!profile) return;
  setDraft({ ...profile.params });
  setTypesText((profile.params?.lawyerFacingProjectTypes || []).join(', '));
}

function ParamField({
  paramKey,
  draft,
  onNumberChange,
  readOnly = false,
  t,
}) {
  return (
    <div className="col-md-4 mb-2">
      <label>
        <ParamTooltip paramKey={paramKey}>
          {t(`match.param.${paramKey}`)}
        </ParamTooltip>
      </label>
      <input
        type="number"
        step="any"
        className="form-control form-control-sm"
        value={Number.isFinite(draft[paramKey]) ? draft[paramKey] : ''}
        onChange={(e) => onNumberChange(paramKey, e.target.value)}
        readOnly={readOnly}
        disabled={readOnly}
      />
    </div>
  );
}

function ToggleField({ paramKey, draft, updateDraft, t }) {
  return (
    <div className="col-md-4 mb-2">
      <label>
        <input
          type="checkbox"
          checked={draft[paramKey] !== false}
          onChange={(e) => updateDraft(paramKey, e.target.checked)}
        />
        {' '}
        <ParamTooltip paramKey={paramKey}>
          {t(`match.param.${paramKey}`)}
        </ParamTooltip>
      </label>
    </div>
  );
}

function SectionWarning({ children }) {
  if (!children) return null;
  return <div className="match-param-warning">{children}</div>;
}

function FeedbackBar({ children }) {
  return <div className="match-param-feedback">{children}</div>;
}

export function ParamsTab({
  onProfileChange,
  inquiryText = '',
  onInquiryTextChange,
  classifierCache,
  onClassifierCacheChange,
  onSaveStateChange,
}) {
  const { t } = useTranslation();
  const [profiles, setProfiles] = useState([]);
  const [classifierCatalogue, setClassifierCatalogue] = useState({ models: [], pricesReviewedAt: '', estimatedOutputTokens: 150 });
  const [fieldListForEstimate, setFieldListForEstimate] = useState([]);
  const [selectedName, setSelectedName] = useState('');
  const [compareName, setCompareName] = useState('');
  const [draft, setDraft] = useState({});
  const [newName, setNewName] = useState('');
  const [diff, setDiff] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewDone, setPreviewDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [setAsDefault, setSetAsDefault] = useState(true);
  const [typesText, setTypesText] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [classifying, setClassifying] = useState(false);
  const feedbackTimer = useRef(null);

  const buildClassifyKeyForDraft = useCallback((params) => (
    buildClassifyKey(inquiryText, params)
  ), [inquiryText]);

  const buildDraftParams = useCallback(() => ({
    ...draft,
    lawyerFacingProjectTypes: typesText.split(',').map((s) => s.trim()).filter(Boolean),
    selectionMode: draft.selectionMode || 'softmax',
  }), [draft, typesText]);

  const loadProfiles = useCallback(async (selectName) => {
    const data = await axios.get('/match/params');
    const list = data.profiles || [];
    setProfiles(list);
    setClassifierCatalogue(data.classifierCatalogue || { models: [], pricesReviewedAt: '', estimatedOutputTokens: 150 });
    setFieldListForEstimate(data.fieldListForEstimate || []);
    const def = data.defaultProfile || list[0];
    const nameToSelect = selectName || selectedName || def?.name;
    const profile = list.find((p) => p.name === nameToSelect) || def;
    if (profile) {
      setSelectedName(profile.name);
      applyProfileToState(profile, setDraft, setTypesText);
      onProfileChange?.(profile.name);
    }
    return { list, profile };
  }, [onProfileChange, selectedName]);

  useEffect(() => { loadProfiles(); }, []);

  const selectedProfile = profiles.find((p) => p.name === selectedName);

  useEffect(() => {
    if (selectedProfile) {
      applyProfileToState(selectedProfile, setDraft, setTypesText);
      setPreviewDone(false);
      setPreview(null);
    }
  }, [selectedName]);

  const normalized = useMemo(() => normalizeWeights(draft), [draft]);
  const maturityNormalized = useMemo(() => normalizeMaturityWeights(draft), [draft]);
  const weightBar = useMemo(() => fmtWeightBar(normalized), [normalized]);
  const qDelta = useMemo(() => expertiseQDelta(draft), [draft]);
  const speeds = useMemo(() => speedTable(draft), [draft]);
  const singleRating = useMemo(() => ratingForSingle(draft), [draft]);
  const dExamples = useMemo(() => dAtBreachRates(draft), [draft]);
  const softmaxRatioVal = useMemo(
    () => softmaxRatio(0.65, 0.63, draft.T ?? 0.10),
    [draft.T],
  );
  const wWarnings = useMemo(() => weightWarnings(draft), [draft]);

  const classifierProvider = draft.classifierProvider || 'openai';
  const classifierModel = draft.classifierModel || 'gpt-4o';
  const selectedModelEntry = useMemo(
    () => (classifierCatalogue?.models || []).find(
      (entry) => entry.provider === classifierProvider && entry.model === classifierModel,
    ),
    [classifierCatalogue, classifierProvider, classifierModel],
  );
  const modelCapabilities = useMemo(
    () => getClassifierModelCapabilities(
      classifierProvider,
      classifierModel,
      selectedModelEntry,
    ),
    [classifierProvider, classifierModel, selectedModelEntry],
  );
  const supportsClassifierThinking = modelCapabilities.supportsThinking === true;
  const supportsClassifierEffort = modelCapabilities.supportsEffort === true;
  const classifierEffortLevels = modelCapabilities.effortLevels || [];
  const effectiveClassifierThinking = draft.classifierThinking
    ?? modelCapabilities.thinkingDefault
    ?? 'off';
  const effectiveClassifierEffort = draft.classifierEffort
    ?? modelCapabilities.defaultEffort
    ?? 'high';
  const catalogueGroups = useMemo(
    () => groupCatalogueByProvider(classifierCatalogue),
    [classifierCatalogue],
  );
  const resolvedPromptEstimate = useMemo(
    () => resolvePromptForEstimate(
      draft.classifierPrompt,
      fieldListForEstimate,
      inquiryText || '',
      {
        detail: draft.classifierFieldDetail || 'full',
        format: draft.classifierFieldFormat || 'json',
      },
    ),
    [
      draft.classifierPrompt,
      draft.classifierFieldDetail,
      draft.classifierFieldFormat,
      fieldListForEstimate,
      inquiryText,
    ],
  );
  const fieldsOnlyEstimate = useMemo(() => {
    const built = buildFieldListForPrompt(
      fieldListForEstimate,
      draft.classifierFieldDetail || 'full',
    );
    return estimateTokens(serializeFieldList(built, draft.classifierFieldFormat || 'json'));
  }, [fieldListForEstimate, draft.classifierFieldDetail, draft.classifierFieldFormat]);
  const classifierCostEstimate = useMemo(
    () => estimateClassifierCost({
      catalogue: classifierCatalogue,
      provider: classifierProvider,
      model: classifierModel,
      resolvedPrompt: resolvedPromptEstimate,
    }),
    [classifierCatalogue, classifierProvider, classifierModel, resolvedPromptEstimate],
  );

  const handleClassifierModelChange = (value) => {
    const [provider, model] = value.split('::');
    const defaults = defaultThinkingEffortForModel(model);
    setDraft((d) => {
      const next = {
        ...d,
        classifierProvider: provider,
        classifierModel: model,
      };
      if (defaults) {
        next.classifierThinking = defaults.classifierThinking;
        next.classifierEffort = defaults.classifierEffort;
      } else {
        delete next.classifierThinking;
        delete next.classifierEffort;
      }
      if (provider !== 'anthropic') {
        delete next.classifierCacheControl;
      }
      return next;
    });
    setPreviewDone(false);
  };

  const formatModelOptionLabel = (entry) => {
    const est = estimateClassifierCost({
      catalogue: classifierCatalogue,
      provider: entry.provider,
      model: entry.model,
      resolvedPrompt: resolvedPromptEstimate,
    });
    const ctx = formatContextWindow(entry.contextWindow);
    const perCall = est ? formatCostUsd(est.costUsd) : '—';
    const per1k = est ? formatCostUsd(est.per1000Usd) : '—';
    return `${entry.label} — ${ctx} ctx — ${perCall}/call, ${per1k}/1k`;
  };

  const fetchFeedback = useCallback(async (params) => {
    try {
      const cacheKey = buildClassifyKeyForDraft(params);
      const cachedResults = classifierCache?.key === cacheKey ? classifierCache.results : null;
      const result = await axios.post('/match/params/feedback', {
        params,
        currentProfile: selectedName,
        field_id: 10,
        lang: 'et',
        clientType: 'person',
        paramsProfile: selectedName,
        text: inquiryText.trim() || undefined,
        classifierResults: cachedResults || undefined,
      });
      if (!isApiError(result)) {
        setFeedback(result);
      }
    } catch {
      /* ignore transient feedback errors */
    }
  }, [selectedName, inquiryText, classifierCache, buildClassifyKeyForDraft]);

  const classifyInquiry = async () => {
    if (!inquiryText.trim()) return;
    setClassifying(true);
    try {
      const params = buildDraftParams();
      const data = await axios.post('/match/classify', {
        text: inquiryText,
        paramsProfile: selectedName,
      });
      const key = buildClassifyKeyForDraft(params);
      onClassifierCacheChange?.({ text: inquiryText, results: data.results || [], key });
      fetchFeedback(params);
    } finally {
      setClassifying(false);
    }
  };

  useEffect(() => {
    if (!selectedName || !Object.keys(draft).length) return undefined;
    if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
    feedbackTimer.current = setTimeout(() => {
      fetchFeedback(buildDraftParams());
    }, 400);
    return () => {
      if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
    };
  }, [draft, typesText, selectedName, buildDraftParams, fetchFeedback, inquiryText]);

  const fieldFeedback = feedback?.fieldFeedback;

  const updateDraft = (key, value) => {
    setDraft((d) => {
      const next = { ...d };
      if (value === '' || value == null || (typeof value === 'number' && !Number.isFinite(value))) {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
    setPreviewDone(false);
  };

  const updateDisplay = (section, field, value) => {
    let normalized = value;
    if (value === '' || (typeof value === 'number' && !Number.isFinite(value))) {
      normalized = null;
    }
    setDraft((d) => ({
      ...d,
      display: {
        ...(d.display || {}),
        [section]: {
          ...(d.display?.[section] || {}),
          [field]: normalized,
        },
      },
    }));
    setPreviewDone(false);
  };

  const handleNumberChange = (key, raw) => {
    if (raw === '' || raw == null) {
      updateDraft(key, null);
      return;
    }
    const num = parseFloat(raw);
    updateDraft(key, Number.isFinite(num) ? num : null);
  };

  const runPreviewInternal = async () => {
    const params = buildDraftParams();
    const result = await axios.post('/match/params/preview', {
      params,
      currentProfile: selectedName,
      field_id: 10,
      lang: 'et',
      clientType: 'person',
    });
    if (isApiError(result)) {
      return { ok: false, result };
    }
    setPreview(result);
    setPreviewDone(true);
    return { ok: true, result };
  };

  const runPreview = async () => {
    const { ok, result } = await runPreviewInternal();
    if (!ok) {
      notifyMatchError(result?.message, t('match.previewFailed'));
    }
  };

  const runDiff = async () => {
    if (!compareName) return;
    const result = await axios.get('/match/params/diff', {
      params: { profileA: selectedName, profileB: compareName },
    });
    setDiff(result.diff);
  };

  const mergeSavedProfile = (savedProfile) => {
    if (!savedProfile) return;
    setProfiles((prev) => {
      const idx = prev.findIndex((p) => p.name === savedProfile.name);
      if (idx === -1) {
        return [...prev, savedProfile];
      }
      const next = [...prev];
      if (savedProfile.isDefault) {
        next.forEach((p) => { p.isDefault = false; });
      }
      next[idx] = savedProfile;
      return next;
    });
    setSelectedName(savedProfile.name);
    applyProfileToState(savedProfile, setDraft, setTypesText);
    onProfileChange?.(savedProfile.name);
    setPreviewDone(false);
    setPreview(null);
  };

  const saveCurrentProfile = async () => {
    if (!selectedName) {
      notifyMatchError(t('match.noProfileSelected'));
      return;
    }
    setSaving(true);
    try {
      if (!previewDone) {
        const { ok, result } = await runPreviewInternal();
        if (!ok) {
          notifyMatchError(result?.message, t('match.previewFailedBeforeSave'));
          return;
        }
      }

      const params = buildDraftParams();
      const result = await axios.put(`/match/params/${encodeURIComponent(selectedName)}`, {
        params,
        isDefault: setAsDefault,
      });
      if (isApiError(result)) {
        notifyMatchError(result.message, t('match.saveFailed'));
        return;
      }

      mergeSavedProfile(result.profile);
      window.nSuccess?.(t('match.savedCurrent', { name: result.profile?.name || selectedName }));
    } finally {
      setSaving(false);
    }
  };

  const saveCurrentProfileRef = useRef(saveCurrentProfile);
  saveCurrentProfileRef.current = saveCurrentProfile;

  const saveAsNewVersion = async () => {
    setSaving(true);
    try {
      if (!previewDone) {
        const { ok, result } = await runPreviewInternal();
        if (!ok) {
          notifyMatchError(result?.message, t('match.previewFailedBeforeSave'));
          return;
        }
      }

      const params = buildDraftParams();
      const trimmedName = newName.trim() || generateAutoName(selectedName);
      const result = await axios.post('/match/params', {
        name: trimmedName,
        params,
        isDefault: setAsDefault,
      });
      if (isApiError(result)) {
        notifyMatchError(result.message, t('match.saveFailed'));
        return;
      }

      const savedName = result.profile?.name || trimmedName;
      setNewName('');
      await loadProfiles(savedName);
      setPreviewDone(false);
      setPreview(null);

      const successKey = setAsDefault ? 'match.savedAsDefault' : 'match.savedAsNamed';
      window.nSuccess?.(t(successKey, { name: savedName }));
    } finally {
      setSaving(false);
    }
  };

  const nonDisc = feedback?.nonDiscriminating || [];

  useEffect(() => {
    if (!onSaveStateChange) {
      return undefined;
    }
    onSaveStateChange({
      onSave: () => saveCurrentProfileRef.current?.(),
      saving,
      disabled: saving || !selectedName,
    });
    return () => onSaveStateChange(null);
  }, [onSaveStateChange, saving, selectedName]);

  return (
    <div className="match-params">
      <div className="row mb-3 align-items-end">
        <div className="col-md-4">
          <label>{t('match.loadProfile')}</label>
          <select className="form-control" value={selectedName} onChange={(e) => {
            setSelectedName(e.target.value);
            onProfileChange?.(e.target.value);
          }}>
            {profiles.map((p) => <option key={p.name} value={p.name}>{p.name}{p.isDefault ? ' (default)' : ''}</option>)}
          </select>
        </div>
        <div className="col-md-4">
          <label>{t('match.compareProfile')}</label>
          <select className="form-control" value={compareName} onChange={(e) => setCompareName(e.target.value)}>
            <option value="">—</option>
            {profiles.filter((p) => p.name !== selectedName).map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
          </select>
          <button type="button" className="btn btn-sm btn-secondary mt-1" onClick={runDiff}>{t('match.showDiff')}</button>
        </div>
        <div className="col-md-4 match-params-actions">
          <button type="button" className="btn btn-primary" disabled={saving} onClick={runPreview}>
            {t('match.previewChanges')}
          </button>
          <label className="d-block mt-2 mb-0">
            <input type="checkbox" checked={setAsDefault} onChange={(e) => setSetAsDefault(e.target.checked)} />
            {' '}{t('match.setAsDefault')}
          </label>
        </div>
      </div>

      <p className="text-muted small">{t('match.saveCurrentHelper', { name: selectedName || '—' })}</p>

      {/* Section: fields */}
      <section id="match-params-fields" className="match-param-section">
        <h5>{t('match.section.fields.title')}</h5>
        <p className="match-param-section-desc">{t('match.section.fields.desc')}</p>
        <div className="form-row col-full mb-2">
          <label>{t('match.inquiryText')}</label>
          <textarea
            className="form-control"
            rows={3}
            value={inquiryText}
            onChange={(e) => onInquiryTextChange?.(e.target.value)}
          />
          <button
            type="button"
            className="btn btn-secondary btn-sm mt-2"
            onClick={classifyInquiry}
            disabled={classifying || !inquiryText.trim()}
          >
            {classifying ? t('common.loading') : t('match.classify')}
          </button>
        </div>
        <div className="row">
          <div className="col-md-4 mb-2">
            <label>
              <ParamTooltip paramKey="classifierFieldCount">
                {t('match.param.classifierFieldCount')}
              </ParamTooltip>
            </label>
            <select
              className="form-control form-control-sm"
              value={draft.classifierFieldCount ?? 1}
              onChange={(e) => updateDraft('classifierFieldCount', Number(e.target.value))}
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
            </select>
          </div>
          <div className="col-md-4 mb-2">
            <label>
              <ParamTooltip paramKey="fieldMatchMode">
                {t('match.param.fieldMatchMode')}
              </ParamTooltip>
            </label>
            <select
              className="form-control form-control-sm"
              value={draft.fieldMatchMode || 'any'}
              onChange={(e) => updateDraft('fieldMatchMode', e.target.value)}
            >
              <option value="any">any</option>
              <option value="all">all</option>
              <option value="first_required">first_required</option>
            </select>
          </div>
          <ParamField paramKey="classifierMinConfidence" draft={draft} onNumberChange={handleNumberChange} t={t} />
          <div className="col-md-4 mb-2">
            <label>
              <ParamTooltip paramKey="classifierFieldDetail">
                {t('match.param.classifierFieldDetail')}
              </ParamTooltip>
            </label>
            <select
              className="form-control form-control-sm"
              value={draft.classifierFieldDetail || 'full'}
              onChange={(e) => updateDraft('classifierFieldDetail', e.target.value)}
            >
              {CLASSIFIER_FIELD_DETAILS.map((level) => (
                <option key={level} value={level}>
                  {t(`match.classifierFieldDetail.${level}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-4 mb-2">
            <label>
              <ParamTooltip paramKey="classifierFieldFormat">
                {t('match.param.classifierFieldFormat')}
              </ParamTooltip>
            </label>
            <select
              className="form-control form-control-sm"
              value={draft.classifierFieldFormat || 'json'}
              onChange={(e) => updateDraft('classifierFieldFormat', e.target.value)}
            >
              {CLASSIFIER_FIELD_FORMATS.map((fmt) => (
                <option key={fmt} value={fmt}>
                  {t(`match.classifierFieldFormat.${fmt}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-8 mb-2">
            <label>
              <ParamTooltip paramKey="classifierModel">
                {t('match.param.classifierModel')}
              </ParamTooltip>
            </label>
            <select
              className="form-control form-control-sm"
              value={`${classifierProvider}::${classifierModel}`}
              onChange={(e) => handleClassifierModelChange(e.target.value)}
            >
              {['openai', 'anthropic'].map((providerKey) => {
                const entries = catalogueGroups[providerKey] || [];
                if (!entries.length) return null;
                return (
                  <optgroup key={providerKey} label={t(`match.classifierProvider.${providerKey}`)}>
                    {entries.map((entry) => (
                      <option
                        key={`${entry.provider}::${entry.model}`}
                        value={`${entry.provider}::${entry.model}`}
                        disabled={entry.available === false}
                      >
                        {formatModelOptionLabel(entry)}
                        {entry.available === false ? ` (${t('match.classifierUnavailable')})` : ''}
                      </option>
                    ))}
                  </optgroup>
                );
              })}
            </select>
            {classifierCostEstimate && (
              <>
                <p className="small text-muted mb-0 mt-1" title={t('match.classifierCostTooltip')}>
                  {t('match.classifierCostEstimate', {
                    perCall: formatCostUsd(classifierCostEstimate.costUsd),
                    per1k: formatCostUsd(classifierCostEstimate.per1000Usd),
                    reviewed: classifierCatalogue.pricesReviewedAt || '—',
                  })}
                </p>
                <p className="small text-muted mb-0">
                  {t('match.classifierFieldCostBreakdown', {
                    fieldsTokens: fieldsOnlyEstimate,
                    totalTokens: classifierCostEstimate.inputTokens,
                    detail: draft.classifierFieldDetail || 'full',
                    format: draft.classifierFieldFormat || 'json',
                  })}
                </p>
              </>
            )}
            {(catalogueGroups.anthropic || []).some((m) => m.available === false) && (
              <p className="small text-warning mb-0">
                {t('match.classifierAnthropicDisabled')}
              </p>
            )}
          </div>
          <div className="col-12 mb-2">
            {supportsClassifierThinking || supportsClassifierEffort ? (
              <div className="row">
                {supportsClassifierThinking && (
                  <div className="col-md-4 mb-2">
                    <label>
                      <ParamTooltip paramKey="classifierThinking">
                        {t('match.param.classifierThinking')}
                      </ParamTooltip>
                    </label>
                    <select
                      className="form-control form-control-sm"
                      value={effectiveClassifierThinking}
                      onChange={(e) => updateDraft('classifierThinking', e.target.value)}
                    >
                      <option value="off">{t('match.classifierThinking.off')}</option>
                      <option value="adaptive">{t('match.classifierThinking.adaptive')}</option>
                    </select>
                  </div>
                )}
                {supportsClassifierEffort && (
                  <div className="col-md-4 mb-2">
                    <label>
                      <ParamTooltip paramKey="classifierEffort">
                        {t('match.param.classifierEffort')}
                      </ParamTooltip>
                    </label>
                    <select
                      className="form-control form-control-sm"
                      value={effectiveClassifierEffort}
                      onChange={(e) => updateDraft('classifierEffort', e.target.value)}
                    >
                      {classifierEffortLevels.map((level) => (
                        <option key={level} value={level}>
                          {t(`match.classifierEffort.${level}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {classifierProvider === 'anthropic' && (
                  <div className="col-md-4 mb-2 d-flex align-items-end">
                    <div className="form-check mb-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="classifierCacheControl"
                        checked={draft.classifierCacheControl === true}
                        onChange={(e) => updateDraft('classifierCacheControl', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="classifierCacheControl">
                        <ParamTooltip paramKey="classifierCacheControl">
                          {t('match.param.classifierCacheControl')}
                        </ParamTooltip>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            ) : classifierProvider === 'anthropic' ? (
              <div className="row">
                <div className="col-md-4 mb-2 d-flex align-items-end">
                  <div className="form-check mb-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="classifierCacheControlHaiku"
                      checked={draft.classifierCacheControl === true}
                      onChange={(e) => updateDraft('classifierCacheControl', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="classifierCacheControlHaiku">
                      <ParamTooltip paramKey="classifierCacheControl">
                        {t('match.param.classifierCacheControl')}
                      </ParamTooltip>
                    </label>
                  </div>
                </div>
              </div>
            ) : (
              <p className="small text-muted mb-0">
                {t('match.classifierThinkingEffortHint')}
              </p>
            )}
          </div>
          <div className="col-12 mb-2">
            <label>
              <ParamTooltip paramKey="classifierPrompt">
                {t('match.param.classifierPrompt')}
              </ParamTooltip>
            </label>
            <textarea
              className="form-control form-control-sm font-monospace"
              rows={8}
              value={draft.classifierPrompt || ''}
              onChange={(e) => updateDraft('classifierPrompt', e.target.value)}
            />
          </div>
        </div>
        <h6 className="mt-3">{t('match.section.coverage.title')}</h6>
        <p className="match-param-section-desc small">{t('match.section.coverage.desc')}</p>
        <div className="row">
          <ParamField paramKey="coverageWeight" draft={draft} onNumberChange={handleNumberChange} t={t} />
          {[0, 1, 2].map((idx) => (
            <div key={idx} className="col-md-4 mb-2">
              <label>{t('match.fieldRankWeight', { rank: idx + 1 })}</label>
              <input
                type="number"
                step="any"
                className="form-control form-control-sm"
                value={Number.isFinite((draft.fieldRankWeights || [])[idx])
                  ? draft.fieldRankWeights[idx]
                  : ''}
                onChange={(e) => {
                  const next = [...(draft.fieldRankWeights || [1, 0.5, 0.25])];
                  next[idx] = Number(e.target.value);
                  updateDraft('fieldRankWeights', next);
                }}
              />
            </div>
          ))}
        </div>
        <FeedbackBar>
          {fieldFeedback?.poolSizes && (
            <p className="mb-1 small">
              {t('match.poolSizes')}:{' '}
              {t('match.feedback.poolSizes', {
                any: fieldFeedback.poolSizes.any ?? 0,
                all: fieldFeedback.poolSizes.all ?? 0,
                firstRequired: fieldFeedback.poolSizes.first_required ?? 0,
              })}
            </p>
          )}
          {(fieldFeedback?.classifiedFields || []).length > 0 && (
            <ul className="small mb-1">
              {fieldFeedback.classifiedFields.map((f) => {
                let mark = '';
                if (f.selected) mark = ' ✓';
                else if (f.dropped) {
                  mark = ` (${t(`match.dropReason.${f.dropReason || 'count_limit'}`)})`;
                }
                return (
                  <li key={f.field_id}>
                    {t('match.feedback.classifiedField', {
                      fieldId: f.field_id,
                      name: f.name,
                      confidence: fmtNum((f.confidence ?? 0) * 100) + '%',
                      mark,
                    })}
                  </li>
                );
              })}
            </ul>
          )}
          {fieldFeedback?.fieldLawyerCounts && Object.keys(fieldFeedback.fieldLawyerCounts).length > 0 && (
            <p className="mb-0 small">
              {t('match.fieldLawyerCounts')}:{' '}
              {Object.entries(fieldFeedback.fieldLawyerCounts).map(([fieldId, count]) => (
                <span key={fieldId} className="me-2">
                  {t('match.feedback.fieldLawyerCount', { fieldId, count })}
                </span>
              ))}
            </p>
          )}
          {fieldFeedback?.classifierWarning && (
            <SectionWarning>{fieldFeedback.classifierWarning}</SectionWarning>
          )}
          {feedback?.effectiveFieldRankWeights?.length > 0 && (
            <p className="mb-0 small">
              {t('match.feedback.effectiveFieldRankWeights', {
                weights: feedback.effectiveFieldRankWeights.map((w) => fmtNum(w)).join(' · '),
                padded: feedback.fieldRankWeightsDidPad ? t('match.feedback.fieldRankWeightsPadded') : '',
              })}
            </p>
          )}
        </FeedbackBar>
      </section>

      {/* Section 1 */}
      <section id="match-params-weights" className="match-param-section">
        <h5>{t('match.section.weights.title')}</h5>
        <p className="match-param-section-desc">{t('match.section.weights.desc')}</p>
        <div className="row">
          <ParamField paramKey="wE" draft={draft} onNumberChange={handleNumberChange} t={t} />
          <ParamField paramKey="wR" draft={draft} onNumberChange={handleNumberChange} t={t} />
          <ParamField paramKey="wS" draft={draft} onNumberChange={handleNumberChange} t={t} />
          <ToggleField paramKey="toggleE" draft={draft} updateDraft={updateDraft} t={t} />
          <ToggleField paramKey="toggleR" draft={draft} updateDraft={updateDraft} t={t} />
          <ToggleField paramKey="toggleS" draft={draft} updateDraft={updateDraft} t={t} />
        </div>
        <FeedbackBar>
          <p className="mb-1 small">
            {t('match.feedback.normalized')}: wE={fmtNum(normalized.wE)}, wR={fmtNum(normalized.wR)}, wS={fmtNum(normalized.wS)}
          </p>
          <div className="match-weight-bar" aria-hidden>
            {weightBar.map((seg) => (
              <div
                key={seg.key}
                className="match-weight-bar-seg"
                style={{ width: `${seg.pct * 100}%`, background: seg.color }}
                title={`${seg.key}: ${fmtNum(seg.pct)}`}
              />
            ))}
          </div>
        </FeedbackBar>
        {wWarnings.map((w) => (
          <SectionWarning key={w.key}>
            {t('match.warn.weightZero', { component: w.label })}
          </SectionWarning>
        ))}
        {nonDisc.includes('E') && <SectionWarning>{t('match.warn.componentFlat', { component: 'E' })}</SectionWarning>}
        {nonDisc.includes('R') && <SectionWarning>{t('match.warn.componentFlat', { component: 'R' })}</SectionWarning>}
        {nonDisc.includes('S') && <SectionWarning>{t('match.warn.componentFlat', { component: 'S' })}</SectionWarning>}
      </section>

      {/* Section 2 */}
      <section id="match-params-expertise" className="match-param-section">
        <h5>{t('match.section.expertise.title')}</h5>
        <p className="match-param-section-desc">{t('match.section.expertise.desc')}</p>
        <div className="row">
          <ParamField paramKey="eTop5" draft={draft} onNumberChange={handleNumberChange} t={t} />
          <ParamField paramKey="eDeclared" draft={draft} onNumberChange={handleNumberChange} t={t} />
        </div>
        <FeedbackBar>
          <p className="mb-0 small">
            {t('match.feedback.expertiseDelta', { delta: fmtNum(qDelta) })}
          </p>
        </FeedbackBar>
        {(draft.eDeclared ?? 0) === 0 && (feedback?.anyTop5Count ?? 0) === 0 && (
          <SectionWarning>{t('match.warn.expertiseInert')}</SectionWarning>
        )}
      </section>

      {/* Section 3 */}
      <section id="match-params-speed" className="match-param-section">
        <h5>{t('match.section.speed.title')}</h5>
        <p className="match-param-section-desc">{t('match.section.speed.desc')}</p>
        <div className="row">
          <ParamField paramKey="businessDayStart" draft={draft} onNumberChange={handleNumberChange} t={t} />
          <ParamField paramKey="businessDayEnd" draft={draft} onNumberChange={handleNumberChange} t={t} />
          <ParamField paramKey="tau" draft={draft} onNumberChange={handleNumberChange} t={t} />
          <ParamField paramKey="t0" draft={draft} onNumberChange={handleNumberChange} t={t} />
          <ParamField paramKey="m_S" draft={draft} onNumberChange={handleNumberChange} t={t} />
          <ToggleField paramKey="toggleBusinessHoursSpeed" draft={draft} updateDraft={updateDraft} t={t} />
        </div>
        <FeedbackBar>
          <table className="table table-sm table-bordered match-speed-feedback-table mb-0">
            <thead>
              <tr>
                <th>{draft.toggleBusinessHoursSpeed !== false
                  ? t('match.feedback.businessHours')
                  : t('match.feedback.hours')}</th>
                {speeds.map((s) => <th key={s.hours}>{s.hours}h</th>)}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>S</td>
                {speeds.map((s) => <td key={s.hours}>{fmtNum(s.S)}</td>)}
              </tr>
            </tbody>
          </table>
          {feedback && (
            <p className="mb-1 small mt-2">
              {t('match.feedback.platformMedian', {
                hours: feedback.platformMedianRespH != null ? fmtNum(feedback.platformMedianRespH) : '—',
              })}
            </p>
          )}
          {feedback?.speedSStats && (
            <p className="mb-0 small">
              {t('match.feedback.speedDistribution', {
                min: fmtNum(feedback.speedSStats.min),
                median: fmtNum(feedback.speedSStats.median),
                max: fmtNum(feedback.speedSStats.max),
              })}
            </p>
          )}
        </FeedbackBar>
      </section>

      {/* Section 4 */}
      <section id="match-params-rating" className="match-param-section">
        <h5>{t('match.section.rating.title')}</h5>
        <p className="match-param-section-desc">{t('match.section.rating.desc')}</p>
        <div className="row">
          <ParamField paramKey="r0" draft={draft} onNumberChange={handleNumberChange} t={t} />
          <ParamField paramKey="m_R" draft={draft} onNumberChange={handleNumberChange} t={t} />
          <div className="col-md-4 mb-2">
            <label>{t('match.param.m_S')}</label>
            <input type="number" className="form-control form-control-sm" value={draft.m_S ?? ''} readOnly disabled />
            <a href="#match-params-speed" className="small">{t('match.feedback.editInSection3')}</a>
          </div>
          <div className="col-md-4 mb-2">
            <label>{t('match.param.t0')}</label>
            <input type="number" className="form-control form-control-sm" value={draft.t0 ?? ''} readOnly disabled />
            <a href="#match-params-speed" className="small">{t('match.feedback.editInSection3')}</a>
          </div>
        </div>
        <FeedbackBar>
          <p className="mb-1 small">
            {t('match.feedback.singleRating', { R: fmtNum(singleRating.R), rStar: fmtNum(singleRating.rStar) })}
          </p>
          {feedback && (
            <p className="mb-0 small">
              {t('match.feedback.missingRatings', {
                missing: feedback.missingRatingCount,
                total: feedback.activeCount,
              })}
            </p>
          )}
        </FeedbackBar>
        {feedback && feedback.activeCount > 0
          && feedback.missingRatingCount > feedback.activeCount / 2 && (
          <SectionWarning>{t('match.warn.mRDominated')}</SectionWarning>
        )}
      </section>

      {/* Section 5 */}
      <section id="match-params-load" className="match-param-section">
        <h5>{t('match.section.load.title')}</h5>
        <p className="match-param-section-desc">{t('match.section.load.desc')}</p>
        <div className="row">
          <ParamField paramKey="weeklyIntakeCap" draft={draft} onNumberChange={handleNumberChange} t={t} />
          <ParamField paramKey="backlogCap" draft={draft} onNumberChange={handleNumberChange} t={t} />
          <ParamField paramKey="theta" draft={draft} onNumberChange={handleNumberChange} t={t} />
          <ParamField paramKey="waitingMaxAgeDays" draft={draft} onNumberChange={handleNumberChange} t={t} />
          <ToggleField paramKey="toggleFlowLoad" draft={draft} updateDraft={updateDraft} t={t} />
          <ToggleField paramKey="toggleBacklogLoad" draft={draft} updateDraft={updateDraft} t={t} />
          <ToggleField paramKey="toggleA" draft={draft} updateDraft={updateDraft} t={t} />
        </div>
        <FeedbackBar>
          {feedback?.loadStrip?.length > 0 && (
            <div className="match-load-strip">
              {feedback.loadStrip.map((row) => {
                const loadVal = row.loadRaw ?? row.L;
                return (
                <span
                  key={row.user_no}
                  className={`match-load-strip-item${row.A < 1 ? ' penalized' : ''}${row.A === 0 ? ' excluded' : ''}${loadVal > 1 ? ' over-capacity' : ''}`}
                  title={loadVal > 1
                    ? t('match.loadOverCapacity', { L: fmtNum(loadVal, 4) })
                    : `${row.name}: L=${fmtNum(row.L)}, A=${fmtNum(row.A)}`}
                >
                  {row.name?.split(' ')[0] || row.user_no}
                  <small> L{fmtNum(loadVal, loadVal > 1 ? 4 : 3)} A{fmtNum(row.A)}</small>
                </span>
                );
              })}
            </div>
          )}
          {feedback && (
            <p className="mb-0 small mt-2">
              {(feedback.penalizedCount ?? 0) === 0 && (feedback.excludedByLoadCount ?? 0) === 0
                ? t('match.feedback.loadNoEffect')
                : t('match.feedback.loadSummary', {
                  penalized: feedback.penalizedCount ?? 0,
                  excluded: feedback.excludedByLoadCount ?? 0,
                })}
            </p>
          )}
        </FeedbackBar>
        {feedback && (feedback.penalizedCount ?? 0) === 0 && (
          <SectionWarning>{t('match.warn.loadNoEffect')}</SectionWarning>
        )}
      </section>

      {/* Section 6 */}
      <section id="match-params-sla" className="match-param-section">
        <h5>{t('match.section.sla.title')}</h5>
        <p className="match-param-section-desc">{t('match.section.sla.desc')}</p>
        <div className="row">
          <ParamField paramKey="slaBusinessDays" draft={draft} onNumberChange={handleNumberChange} t={t} />
          <ParamField paramKey="slaDayEndHour" draft={draft} onNumberChange={handleNumberChange} t={t} />
          <ParamField paramKey="slaDayEndMinute" draft={draft} onNumberChange={handleNumberChange} t={t} />
          <ParamField paramKey="slaGraceHours" draft={draft} onNumberChange={handleNumberChange} t={t} />
          <ParamField paramKey="slaWindowDays" draft={draft} onNumberChange={handleNumberChange} t={t} />
          <ParamField paramKey="slaHalfLifeDays" draft={draft} onNumberChange={handleNumberChange} t={t} />
          <ParamField paramKey="k_sla" draft={draft} onNumberChange={handleNumberChange} t={t} />
          <ParamField paramKey="D_floor" draft={draft} onNumberChange={handleNumberChange} t={t} />
          <ParamField paramKey="responseOutlierMaxH" draft={draft} onNumberChange={handleNumberChange} t={t} />
          <ToggleField paramKey="toggleSlaAgeing" draft={draft} updateDraft={updateDraft} t={t} />
          <ToggleField paramKey="toggleD" draft={draft} updateDraft={updateDraft} t={t} />
          <ToggleField paramKey="slaCountNoResponseAsBreach" draft={draft} updateDraft={updateDraft} t={t} />
        </div>
        <FeedbackBar>
          {feedback != null && (
            <p className="mb-1 small">
              {t('match.feedback.atDFloor', { count: feedback.atDFloorCount ?? 0 })}
            </p>
          )}
          <table className="table table-sm table-bordered match-speed-feedback-table mb-0">
            <thead>
              <tr>
                <th>v</th>
                {dExamples.map((d) => <th key={d.v}>{Math.round(d.v * 100)}%</th>)}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>D</td>
                {dExamples.map((d) => <td key={d.v}>{fmtNum(d.D)}</td>)}
              </tr>
            </tbody>
          </table>
          {(feedback?.slaAgeingRows || []).length > 0 && (
            <table className="table table-sm table-bordered match-speed-feedback-table mb-0 mt-2">
              <thead>
                <tr>
                  <th>{t('match.colName')}</th>
                  <th>{t('match.feedback.rawBreaches')}</th>
                  <th>{t('match.feedback.weightedBreaches')}</th>
                  <th>v</th>
                  <th>{t('match.feedback.vFlat')}</th>
                  <th>{t('match.feedback.noResponseRate')}</th>
                  <th>{t('match.feedback.noResponseRateLegacy')}</th>
                </tr>
              </thead>
              <tbody>
                {[...feedback.slaAgeingRows]
                  .sort((a, b) => (b.noResponseRateLegacy ?? 0) - (a.noResponseRateLegacy ?? 0))
                  .slice(0, 12)
                  .map((row) => (
                  <tr key={row.user_no} className={(row.noResponseRateLegacy ?? 0) > 1 ? 'match-no-response-legacy-high' : ''}>
                    <td>{row.name || row.user_no}</td>
                    <td>{row.slaBreachCount ?? 0}</td>
                    <td>{fmtNum(row.slaWeightedBreaches)}</td>
                    <td>{fmtNum(row.v)}</td>
                    <td>{fmtNum(row.vFlat)}</td>
                    <td>{fmtNum(row.noResponseRate)}</td>
                    <td>{fmtNum(row.noResponseRateLegacy)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </FeedbackBar>
      </section>

      <DisplayParamsSection draft={draft} updateDisplay={updateDisplay} t={t} />

      {/* Section 7 */}
      <section id="match-params-selection" className="match-param-section">
        <h5>{t('match.section.selection.title')}</h5>
        <p className="match-param-section-desc">{t('match.section.selection.desc')}</p>
        <div className="row">
          <ParamField paramKey="N" draft={draft} onNumberChange={handleNumberChange} t={t} />
          <ParamField paramKey="T" draft={draft} onNumberChange={handleNumberChange} t={t} />
          <ParamField paramKey="maturityWeightDone" draft={draft} onNumberChange={handleNumberChange} t={t} />
          <ParamField paramKey="maturityWeightRating" draft={draft} onNumberChange={handleNumberChange} t={t} />
          <ParamField paramKey="maturityWeightRecent" draft={draft} onNumberChange={handleNumberChange} t={t} />
          <ParamField paramKey="maturityTargetDone" draft={draft} onNumberChange={handleNumberChange} t={t} />
          <ParamField paramKey="maturityTargetRating" draft={draft} onNumberChange={handleNumberChange} t={t} />
          <ParamField paramKey="maturityTargetRecent" draft={draft} onNumberChange={handleNumberChange} t={t} />
          <ParamField paramKey="maturityThreshold" draft={draft} onNumberChange={handleNumberChange} t={t} />
          <div className="col-md-4 mb-2">
            <label>
              <ParamTooltip paramKey="selectionMode">
                {t('match.param.selectionMode')}
              </ParamTooltip>
            </label>
            <select
              className="form-control form-control-sm"
              value={draft.selectionMode || 'softmax'}
              onChange={(e) => updateDraft('selectionMode', e.target.value)}
            >
              <option value="softmax">softmax</option>
              <option value="deterministic">deterministic</option>
            </select>
          </div>
        </div>
        <div className="match-param-unused mt-2">
          <h6 className="text-muted">{t('match.section.unusedParams')}</h6>
          <div className="row">
            <ParamField
              paramKey="newLawyerThreshold"
              draft={draft}
              onNumberChange={handleNumberChange}
              readOnly
              t={t}
            />
          </div>
        </div>
        <FeedbackBar>
          <p className="mb-1 small">
            {t('match.feedback.maturityNormalized', {
              done: fmtNum(maturityNormalized.done),
              rating: fmtNum(maturityNormalized.rating),
              recent: fmtNum(maturityNormalized.recent),
            })}
          </p>
          <div className="match-weight-bar mb-2">
            {fmtMaturityWeightBar(maturityNormalized).map((seg) => (
              <span
                key={seg.key}
                className="match-weight-bar-seg"
                style={{ width: `${seg.pct * 100}%`, background: seg.color }}
                title={`${seg.key}: ${fmtNum(seg.pct)}`}
              />
            ))}
          </div>
          <p className="mb-1 small">
            {t('match.feedback.softmaxRatio', { ratio: fmtNum(softmaxRatioVal) })}
          </p>
          {feedback && (
            <>
              <p className="mb-1 small">
                {t('match.feedback.inclusionSpread', {
                  min: fmtNum(feedback.inclusionMin),
                  max: fmtNum(feedback.inclusionMax),
                })}
              </p>
              <p className="mb-1 small">
                {t('match.feedback.bestInclusion', {
                  pct: fmtNum((feedback.bestInclusionP ?? 0) * 100),
                })}
              </p>
              <p className="mb-1 small">
                {t('match.feedback.maturityEligibleCount', {
                  eligible: feedback.maturityEligibleCount ?? 0,
                  total: feedback.maturityTotal ?? 0,
                  threshold: fmtNum(feedback.maturityThreshold ?? draft.maturityThreshold ?? 0.35),
                })}
              </p>
              {(feedback.maturityEligibleCount === 0 || (feedback.maturityTotal > 0
                && feedback.maturityEligibleCount >= feedback.maturityTotal * 0.9)) && (
                <p className="mb-1 small text-warning">
                  {t('match.feedback.maturityThresholdWarning')}
                </p>
              )}
              {(feedback.maturityDistribution || []).length > 0 && (
                <div className="match-maturity-strip small mb-0">
                  {(feedback.maturityDistribution || []).map((row) => (
                    <span
                      key={row.user_no}
                      className={`match-maturity-dot${row.quotaEligible ? ' match-maturity-dot--eligible' : ''}`}
                      title={`${row.name}: ${fmtNum(row.maturity)}`}
                      style={{ left: `${Math.min(100, Math.max(0, row.maturity * 100))}%` }}
                    />
                  ))}
                  <span
                    className="match-maturity-threshold"
                    style={{ left: `${(feedback.maturityThreshold ?? 0.35) * 100}%` }}
                    title={t('match.maturityThresholdLine')}
                  />
                </div>
              )}
            </>
          )}
        </FeedbackBar>
      </section>

      <div className="form-row col-full mt-2">
        <label>{t('match.lawyerFacingTypes')}</label>
        <input className="form-control" value={typesText} onChange={(e) => { setTypesText(e.target.value); setPreviewDone(false); }} />
      </div>

      {preview && (
        <div className="mt-3">
          <h5>{t('match.previewResults')}</h5>
          <p>{t('match.risers')}: {(preview.risers || []).map((r) => `${r.name}(+${r.shift})`).join(', ') || '—'}</p>
          <p>{t('match.fallers')}: {(preview.fallers || []).map((r) => `${r.name}(${r.shift})`).join(', ') || '—'}</p>
          <table className="table table-sm">
            <thead><tr><th>{t('match.colName')}</th><th>rank before</th><th>rank after</th><th>Σ before</th><th>Σ after</th></tr></thead>
            <tbody>
              {(preview.shifts || []).map((s) => (
                <tr key={s.user_no}>
                  <td>{s.name}</td><td>{s.rankBefore}</td><td>{s.rankAfter}</td>
                  <td>{fmtNum(s.sigmaBefore)}</td><td>{fmtNum(s.sigmaAfter)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 match-params-save-copy">
        <h5>{t('match.saveAsCopyTitle')}</h5>
        <p className="text-muted small mb-2">{t('match.saveHelper')}</p>
        <label>{t('match.newProfileName')}</label>
        <input
          className="form-control"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder={generateAutoName(selectedName)}
        />
        <p className="text-muted small mt-1">{t('match.nameAutoHint')}</p>
        <button type="button" className="btn btn-outline-success mt-2" disabled={saving} onClick={saveAsNewVersion}>
          {saving ? t('common.loading') : t('match.saveProfile')}
        </button>
      </div>

      {diff && (
        <div className="mt-3">
          <h5>{t('match.diff')}</h5>
          <pre style={{ fontSize: 12 }}>{JSON.stringify(diff, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}
